import React from 'react';
import { Bell, X, CheckCircle, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { useUIStore } from '../../stores/uiStore';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Array<{
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'error';
    timestamp: string;
    read: boolean;
  }>;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

export function NotificationsPanel({
  isOpen,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationsPanelProps) {
  const { openModal } = useUIStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
          <div className="pointer-events-auto w-screen max-w-md">
            <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
              {/* Header */}
              <div className="bg-purple-600 py-6 px-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Bell className="h-6 w-6 text-white" />
                    <h2 className="ml-3 text-lg font-medium text-white">Notifications</h2>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        onClose();
                        openModal('settings');
                      }}
                      className="rounded-md bg-purple-500 p-1 text-white hover:bg-purple-400"
                    >
                      <Settings className="h-6 w-6" />
                    </button>
                    <button
                      onClick={onMarkAllAsRead}
                      className="rounded-md bg-purple-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-400"
                    >
                      Mark all as read
                    </button>
                    <button
                      onClick={onClose}
                      className="rounded-md bg-purple-500 text-white hover:bg-purple-400"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-gray-200">
                  {notifications.length === 0 ? (
                    <div className="flex h-full items-center justify-center p-8">
                      <p className="text-center text-gray-500">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`${
                          notification.read ? 'bg-white' : 'bg-purple-50'
                        } p-4 hover:bg-gray-50`}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="ml-3 w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">{notification.message}</p>
                            <p className="mt-1 text-xs text-gray-400">
                              {format(new Date(notification.timestamp), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                          {!notification.read && (
                            <div className="ml-4 flex flex-shrink-0">
                              <button
                                onClick={() => onMarkAsRead(notification.id)}
                                className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500"
                              >
                                <span className="sr-only">Mark as read</span>
                                <CheckCircle className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-400" />;
    case 'warning':
      return <Bell className="h-5 w-5 text-yellow-400" />;
    case 'error':
      return <X className="h-5 w-5 text-red-400" />;
    default:
      return <Bell className="h-5 w-5 text-blue-400" />;
  }
}